import type { GeneratedPackageSettings, PackageSettings } from '@ms-cloudpack/common-types';
import { mergeObjects, mergeArrayDefaults } from '@ms-cloudpack/package-utilities';
import { alwaysExcludeGlobs, sourceFilesGlobs } from '@ms-cloudpack/path-utilities';

/**
 * Merges package settings together.
 *
 * TODO: This "merges" exports maps by concatenating them in an array, which is not correct per the
 * spec in Node. However, merging them properly is much more difficult to implement (depends on the
 * conditions that will be applied at the time the map is used), so more thought would be required
 * for how to handle multiple exports maps properly.
 */
export function mergePackageSettings<TSettings extends Partial<PackageSettings | GeneratedPackageSettings>>(
  settings: TSettings[],
): Omit<TSettings, 'match'> {
  // Use PackageSettings as the type while merging so that the key-based typings work
  // (if the key is not present in generated settings, it does nothing)
  const result = mergeObjects(settings as Partial<PackageSettings>[], {
    // match is deleted after merging, so it is best to overwrite it to avoid merging conflicts.
    overwriteKeys: ['ignoreMissingExports', 'bundlerOptions', 'match'],
    customMerge: {
      exports: (acc, curr) => {
        // Use the current setting if it's explicitly null or was previously not set.
        if (!acc || curr === null) {
          // Make a copy if it's an array, so later additions don't affect the original.
          return Array.isArray(curr) ? [...curr] : curr;
        }

        if (curr) {
          // If we have previous exports but it's not an array, make it an array.
          acc = acc ? (Array.isArray(acc) ? acc : [acc]) : [];
          // Add the new exports.
          acc.push(...(Array.isArray(curr) ? curr : [curr]));
        }

        return acc;
      },
    },
  });

  if (result.inputPaths?.length) {
    const inputPaths = [...result.inputPaths];
    if (!inputPaths.includes('...')) {
      // Add the hardcoded exclusion of node_modules if the user didn't include '...'
      inputPaths.push(...alwaysExcludeGlobs);
    }

    // Dedupe after merging
    const mergedPaths = new Set(mergeArrayDefaults(inputPaths, sourceFilesGlobs));
    if (mergedPaths.has('...')) {
      // Special case: only the first ... token is expanded by mergeArrayDefaults, so delete the
      // extra if there was more than one
      mergedPaths.delete('...');
    }
    result.inputPaths = [...mergedPaths];
  }

  delete result.match;
  return result as TSettings;
}
